import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

export type RecentEntityType = 'client' | 'project' | 'task' | 'ticket';

export interface RecentItem {
  type: RecentEntityType;
  id: string;
  label: string;          // human-readable name shown in the sidebar
  visitedAt: number;      // unix ms
}

const STORAGE_KEY = 'crm_recently_viewed';
const MAX_ITEMS = 8;

function read(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => x && x.type && x.id && x.label) : [];
  } catch {
    return [];
  }
}

// The DOM `storage` event only fires in OTHER windows/tabs when localStorage
// changes, so same-tab writes from `recordRecentlyViewed` / `clear` need a
// separate signal — otherwise the Layout-mounted sidebar holds stale state
// for the whole session until a hard reload.
const subscribers = new Set<() => void>();
const notifyInTab = (): void => { subscribers.forEach((cb) => cb()); };

function write(items: RecentItem[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* quota / private mode */ }
  notifyInTab();
}

/**
 * Reads the recently-viewed list, subscribes to cross-tab changes, and
 * prunes items whose underlying entity has been archived / hard-deleted
 * (or has fallen out of the user's scope) on mount. The list lives in
 * localStorage so it tends to outlive the entities it references —
 * without this, stale entries sit in the sidebar forever.
 */
export function useRecentlyViewed() {
  const [items, setItems] = useState<RecentItem[]>(() => read());

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setItems(read());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    const cb = () => setItems(read());
    subscribers.add(cb);
    return () => { subscribers.delete(cb); };
  }, []);

  // One-shot pruning pass: hand the stored IDs to the backend and keep only
  // the ones it confirms still exist. Soft on failures (network blip / 401
  // during logout) — we never optimistically prune what the server didn't
  // explicitly invalidate.
  useEffect(() => {
    const stored = read();
    if (stored.length === 0) return;
    if (!localStorage.getItem('token')) return; // skip when logged out

    let cancelled = false;
    const payload = stored.map(({ type, id }) => ({ type, id }));

    api.post('/recently-viewed/validate', { items: payload })
      .then((resp) => {
        if (cancelled) return;
        const valid: Array<{ type: RecentEntityType; id: string }> = resp.data?.data?.valid ?? [];
        const validKeys = new Set(valid.map((v) => `${v.type}:${v.id}`));
        const current = read();
        const pruned = current.filter((x) => validKeys.has(`${x.type}:${x.id}`));
        if (pruned.length !== current.length) {
          write(pruned);
          setItems(pruned);
        }
      })
      .catch(() => {
        // Swallow — leaving the list intact is the safe default.
      });

    return () => { cancelled = true; };
  }, []);

  const clear = useCallback(() => { write([]); setItems([]); }, []);

  return { items, clear };
}

/** Imperative recorder — call from a detail page once data is loaded. */
export function recordRecentlyViewed(item: Omit<RecentItem, 'visitedAt'>): void {
  if (!item.id || !item.label) return;
  const current = read();
  const filtered = current.filter((x) => !(x.type === item.type && x.id === item.id));
  const next: RecentItem[] = [{ ...item, visitedAt: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
  write(next);
}
